import { Injectable } from '@angular/core';
import { IPlanData } from '../../plan/models/planData';
import { IStore, StoresService } from './stores.service';
import { HistoryInstance } from '../../plan/models/historyInstance';
import { Stand } from '../../plan/models/stand';
import { PlanogramsService } from './planograms.service';
import { Instrument } from '../../plan/models/instrument';
import { IDevice, InstrumentsService } from './instruments.service';
import { Equipment } from '../../plan/models/equipment';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { lastValueFrom, Observable, of, Subject } from 'rxjs';
import { Wall } from '../../plan/models/wall';
import { IPlansListRequestParams } from '../../plan/models/listRequestParams';
import { ITableData } from '../models/table';
import { Utils } from './utils.service';
import { IPlanListItem } from '../../plan/models/planListItem';
import { Router } from '@angular/router';
import { SnackBarService } from './snack-bar.service';
import { SessionService } from './session.service';
import { DwUsersActivityService } from './dw-users-activity.service';
import { DwUserActivityNames } from '../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../models/dwUsersActivityTypes';
import { NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';

export interface IPlanInfo {
	active: boolean;
	conf: {
		backImageUrl: IBackImageConfig | Array<IBackImageConfig>,
		labels: Array<any>;
		walls: Array<Wall>;
	},
	has_suppliers: boolean;
	id: number;
	image?: string;
	locked: boolean;
	name: string;
	shop_markers?: Array<string>;
	shops: Array<number>;
	trade_area: number;
	update_date: string;
}

export interface IBackImageConfig {
	height: number;
	rotation: number;
	src: string;
	width: number;
	x: number;
	y: number;
	opacity: number;
	client_updated_at: string;
}

export type PlanCreateData = {
	name?: string;
	trade_area?: number;
	shop_id?: number;
	active?: boolean;
}

export interface ITerminal {
	id: number;
	name: string;
	shop_id: number;
}

export interface ICloneItems {
	id: number,
	name: string
}

const ROOT_URL: string = 'api/layout/planograms/',
	NEW_ROOT_URL: string = 'api/v1/plans/';

@Injectable({
	providedIn: 'root'
})
export class PlansService {

	listLoadParams: string = null;
	analyticsLoadParams: string = null;
	laidProductsAnalyticsLoadParams: string = null;
	productHolesAnalyticsLoadParams: string = null;
	changeStatusSubject: Subject<number> = new Subject<number>();

	constructor(
		private router: Router,
		private http: HttpClient,
		private httpService: HttpService,
		private instrumentsService: InstrumentsService,
		private sessionService: SessionService,
		private standsService: PlanogramsService,
		private shopsService: StoresService,
		private snackBarService: SnackBarService,
		private dwUsersActivityService: DwUsersActivityService
	) {}

	bulkClone(params: any = {}): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Copy);

		return this.http.post<void>(NEW_ROOT_URL + 'clone/', {}, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	bulkDelete(params: any = {}): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Delete);

		return this.http.delete<void>(NEW_ROOT_URL, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	async clone(plan: ICloneItems): Promise<IPlanData> {
		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Copy);

		const planogram = await this.httpService.getPromise<IPlanData>(
			this.http.post<IPlanData>(`api/layout/planograms/${plan.id}/clone/`, plan)
		);

		planogram.update_date = new Date(planogram.update_date);
		const shops: Array<IStore> = await this.httpService.getPromise(this.shopsService.getShops$);
		planogram.planogram_shop = shops.filter((shop: IStore) => planogram.planogram_shop.includes(shop.id));

		return planogram;
	}

	create(plan: PlanCreateData): Observable<IPlanData> {
		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Create);

		return this.http.post<IPlanData>(NEW_ROOT_URL, plan);
	}

	downloadEquipments(params: any): Observable<void> {
		return this.http.post<void>('api/v1/plans/export/', null, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	async get(): Promise<Array<IPlanData>> {
		const planograms: Array<IPlanData> = await this.httpService.getPromise<Array<IPlanData>>(
			this.http.get<Array<IPlanData>>(ROOT_URL)
		);

		for (let planogram of planograms) {
			planogram.update_date = new Date(planogram.update_date);
			const shops: Array<IStore> = await this.httpService.getPromise(this.shopsService.getShops$);
			planogram.planogram_shop = shops.filter((shop: IStore) => planogram.planogram_shop.includes(shop.id));
			planogram.shop_markers = planogram.planogram_shop
				.reduce((markers, shop) => [...markers, ...(shop?.markers || [])], []);
		}
		return planograms;
	}

	async getInstruments(id: number): Promise<Array<Instrument>> {
		const instruments: Array<IDevice> = await this.instrumentsService.get(id);

		return instruments.map((device: IDevice) => {
			const instrument: Instrument = new Instrument(device.position_x, device.position_y, device.device_type, device.width, device.depth, device.rotation, device.name);
			instrument.id = device.id;
			instrument.terminalId = device.terminal || null;
			instrument.updated_at = device.updated_at;
			instrument.created_at = device.created_at;
			instrument.client_updated_at = device.client_updated_at;

			return instrument;
		});
	}

	getItem(id: number): Promise<IPlanInfo> {
		return this.httpService.getPromise<IPlanInfo>(
			this.http.get<IPlanInfo>(ROOT_URL + `${id}/`)
		);
	}

	getItem$(id: number): Observable<IPlanInfo | null> {
		return this.http
			.get<IPlanInfo>(ROOT_URL + `${id}/`)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status === 404) {
						this.router.navigate(this.sessionService.getLink(['plan', 'list'])).then(() => {
							this.snackBarService.setSnackBarNotification({
								messageText: 'COMMON.PLAN.NOT_EXIST'
							});
						});
					}
					return of(null)
				}),
				map((plan: IPlanInfo) => {
					if (!plan) return null;

					if (plan.locked) {
						this.router.navigate(this.sessionService.getLink(['plan'])).then(() => {
							this.snackBarService.setSnackBarNotification({
								messageText: 'COMMON.PLAN.LOCKED'
							});
						});

						return null;
					}

					return plan;
				})
			);
	}

	getItems(params: IPlansListRequestParams = {}): Observable<Array<IPlanData>> {
		const httpParams: HttpParams = new HttpParams({
			fromObject: {...params}
		});
		return this.http.get<Array<IPlanData>>(ROOT_URL, { params: httpParams });
	}

	getLibraryLastVersion(planId: number): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.get<any>(`api/layout/stands/last_version/?planogram_id=${planId}`)
		);
	}

	getPlans<T>(params: any = {}, fields: Array<string> = []): Observable<Array<T>> {
		if (fields.length) Utils.assignObject(params, { fields: fields.join(',') });
		return this.http.get<Array<T>>(NEW_ROOT_URL, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getPlanListTable(params: any = {}): Observable<ITableData<IPlanListItem>> {
		return this.http.get<ITableData<IPlanListItem>>('api/v1/plans-table/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getSalesReport(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.post<any>('/api/analytics/planogram/', filters)
		);
	}

	async getStands(id: number, instruments: Array<Equipment>): Promise<Array<Stand>> {
		const stands = await this.standsService.get(id);

		return stands.map((stand) => {
			const instrument = instruments.find(i => i.id == stand.instrument),
				s: Stand = new Stand(
					stand.planogram_stand[0].position_x,
					stand.planogram_stand[0].position_y,
					instrument,
					stand.locked,
					stand.categories,
					stand.planogram_stand[0].rotation,
					stand.name,
					stand.number,
					stand.main_stand,
					stand.type,
					stand.scheduled_date
				);

			s.id = stand.id;
			s.planogramStand = stand.planogram_stand;
			s.tooltip = s.nameWithNumber();
			s.date = stand.updated_at;
			s.was_sent = stand.was_sent ?? false;
			s.image = stand.image;
			s.scheduled_date = stand.scheduled_date;

			if (stand.style_conf.color) s.colorBorder = stand.style_conf.color;
			if (stand.style_conf.fontSize) s.fontSize = stand.style_conf.fontSize;
			if (stand.style_conf.fontColor) s.fontColor = stand.style_conf.fontColor;
			if (stand.style_conf.backgroundColor) s.backgroundColor = stand.style_conf.backgroundColor;
			if (stand.style_conf.colorBorder) s.colorBorder = stand.style_conf.colorBorder;
			if (stand.library) s.library = stand.library;

			s.connectionId = (stand.planogram_stand || [])[0].id;

			return s;
		});
	}

	getStandsRelation(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.post<any>('/api/layout/stand-pair/', filters)
		);
	}

	getStoreManager(shopId: number): Promise<{ user_id: number }> {
		return this.httpService.getPromise<{ user_id: number }>(
			this.http.get<{ user_id: number }>(`/api/profile/shop-manager/?shop_id=${shopId}`)
		);
	}

	getSquareReport(filters: any, fullResponse: boolean = false): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/planogram/square/', filters)
				.pipe(map((response: any) => fullResponse ? response : (response.results || {})))
		);
	}

	getTerminals(id: number): Promise<Array<ITerminal>> {
		return this.httpService.getPromise<Array<ITerminal>>(
			this.http.get<Array<ITerminal>>(`/api/data/terminals/?planogram_id=${id}`)
		);
	}

	loadLaidOutProducts(data: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.post<any>('/api/analytics/planogram/store-point/', data)
		);
	}

	parseAutocad(formData: FormData): Observable<any> {
		return this.http.post<any>(ROOT_URL + 'parse_autocad/', formData);
	}

    searchPlanograms(id: number, query: string, fields: Array<string> = []): Observable<Array<number>> {
        let httpParams: HttpParams = new HttpParams({ fromObject: { query } });
        if (fields.length) httpParams = httpParams.set('fields', fields.join(','));
            
        return this.http.get<Array<number>>(NEW_ROOT_URL + `${id}/search-planograms/`, { params: httpParams });
    }

	async update(plan: IPlanData, data: any): Promise<IPlanData> {
		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Edit);

		data = Object.assign({ name: plan.name, trade_area: plan.trade_area }, data);
		const planId: number = plan.id || plan[0].value.id,
			newPlan: IPlanData = await this.httpService.getPromise<IPlanData>(
				this.http.put<IPlanData>(ROOT_URL + `${planId}/`, data, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
			);
		newPlan.update_date = new Date(plan.update_date);
		const shops: Array<IStore> = await this.httpService.getPromise(this.shopsService.getShops$);
		newPlan.planogram_shop = shops.filter((shop: IStore) => newPlan.planogram_shop.includes(shop.id));
		return newPlan;
	}

	updateNew(id: number, params: PlanCreateData): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Edit);

		return this.http.patch<void>(NEW_ROOT_URL + id + '/', params);
	}

	async updatePlanConf(planInfo: any, firstState: HistoryInstance, endState: HistoryInstance): Promise<IPlanInfo> {
		const pId = planInfo.id,
			{ stands, instruments, walls, labels, backImages } = endState,
			postStands = stands.filter(stand => !stand.id || !stand.connectionId),
			putStands = stands.filter(stand => stand.id && !firstState.stands.find(s => s.isEqual(stand))),
			deleteStands = firstState.stands.filter(s => !stands.find(stand => s.id == stand.id)),
			postInstruments = instruments.filter(i => !i.id),
			putInstruments = instruments.filter(inst => inst.id && !firstState.instruments.find(i => i.isEqual(inst))),
			deleteInstruments = firstState.instruments.filter(i => !instruments.find(stand => i.id == stand.id));

		const deleteStandsIds: Array<number> = deleteStands.map((stand: Stand) => stand.connectionId);
		await this.standsService.bulkCreate(postStands, pId).catch(() => undefined);
		await lastValueFrom(this.standsService.bulkDelete(deleteStandsIds)).catch(() => undefined);

		const promises = [
			...deleteInstruments.map(s => this.instrumentsService.delete(s)),
			...putStands.map(s => {
				const prevStand = firstState.stands.find(st => st.localId == s.localId), promises = [];
				if (!prevStand?.isEqualParams(s)) promises.push(this.standsService.update(s, pId, '?only_update=true'));
				if (!prevStand?.isEqualPosition(s)) promises.push(this.standsService.updatePlanPosition(s, pId));
				return Promise.all(promises);
			}),
			...putInstruments.map(s => this.instrumentsService.update(s, pId)),
			...postInstruments.map(s => this.instrumentsService.create(s, pId))
		];

		await Promise.all(promises.map(p => p.catch(() => undefined)));

		this.dwUsersActivityService.track(DwUserActivityNames.StorePlans, DwUserActivityTypes.Edit);

		return new Promise<IPlanInfo>((resolve, reject) => {
			this.http.put<IPlanInfo>(ROOT_URL + `${pId}/`, {
				name: planInfo.name,
				image: planInfo.image,
				conf: { walls, labels, backImageUrl: backImages },
			}, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }).subscribe({
				next: (res: IPlanInfo) => resolve(res),
				error: (err: Error) => reject(err)
			});
		});

	}

	unlock(id: number): Observable<void> {
		return this.http.post<void>(`${NEW_ROOT_URL}${id}/unlock/`, {});
	}
}